// React
import React, { useMemo, useCallback } from "react";
import { Add } from "@mui/icons-material";
import { Box, Button } from "@mui/material";

// Custom
import AdminPageLayout from "../../components/FullPageLayout/AdminPageLayout";
import Table from "../../components/ReactTable/Table";
import { withTranslation } from "react-i18next";
import { useTranslation } from "react-i18next";
import { withRouter } from "react-router";
import { debounce } from "lodash";
import useConstant from "use-constant";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import AddNewGroupModal from "./AddNewGroupModal";
import { buildGroupDetailInformationRoute } from "../../routes/AdminRoutes";
import { useReport } from "../../hooks/useReport";
import useModal from "../../hooks/useModal";
import { useGroups } from "../../hooks/apiHooks/groups";
import { useTableData } from "../../components/ReactTable/useTableData";
import TextFilter from "../../components/ReactTable/Filters/TextFilter";

// Formik
import { Formik, Form, Field } from "formik";
import { TextField } from "formik-mui";
import { InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TooltipButton from "../../components/Buttons/TooltipButton";
import usePermissions from "../../redux/selectors/usePermissions";
import useUser from "../../hooks/useUser";

const GROUPS_JSONAPI_REQUEST = {
  fields: {
    group: [
      "name",
      "positionsCount",
      "usersCount",
      "locationsCount",
      "customer"
    ],
    customer: ["name"]
  },
  include: ["customer"]
};

const columns = (t, isClient) => {
  const divisionColumn = {
    Header: t("table.columnHeader.OwnedBy"),
    accessor: "customer.name",
    Filter: TextFilter,
    canSort: true,
    filterPlaceholder: t("table.filterPlaceholder.ownedByAll"),
    id: "divisionName"
  };
  const columns = [
    {
      Header: "Name",
      accessor: "name",
      id: "name",
      Filter: TextFilter,
      filterPlaceholder: t("table.filterPlaceholder.nameAll")
    },
    {
      Header: "Locations",
      accessor: "locationsCount",
      id: "locationsCount",
      Filter: TextFilter,
      filterPlaceholder: t("table.filterPlaceholder.locationAll"),
      filterName: "locationsCount"
    },
    {
      Header: "Positions",
      accessor: "positionsCount",
      id: "positionsCount",
      Filter: TextFilter,
      filterPlaceholder: t("table.filterPlaceholder.positionAll"),
      filterName: "positionsCount"
    },
    {
      Header: "Employees",
      accessor: "usersCount",
      id: "usersCount",
      Filter: TextFilter,
      filterPlaceholder: t("table.filterPlaceholder.Employee"),
      filterName: "usersCount"
    }
  ];
  isClient && columns.push(divisionColumn);
  return columns;
};

const GroupsFilter = ({ onSubmit }) => {
  const { t } = useTranslation();
  return (
    <Formik initialValues={{ searchName: "" }} onSubmit={onSubmit}>
      {({ submitForm }) => {
        const debouncedSubmit = useConstant(() => debounce(submitForm, 500));
        return (
          <Form>
            <Box mt={3}>
              <Field
                fullWidth
                label={t("groupsFilters.searchName")}
                variant="filled"
                component={TextField}
                name="name"
                onKeyUp={debouncedSubmit}
                type="text"
                disabled={false}
                InputProps={{
                  disableUnderline: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

const groupsFilters = [{ id: "active", value: true }];

const Groups = ({ history }) => {
  const { getGroups } = useGroups(GROUPS_JSONAPI_REQUEST);
  const { t } = useTranslation();
  const { isGoldOrPlatinum } = usePermissions();
  const { isClient } = useUser();
  const memoColumns = useMemo(() => columns(t, isClient), []);

  const openAddGroupModal = useModal(AddNewGroupModal);
  const openReport = useReport("Training Assignment by Group Report");

  const {
    data: groups,
    paginationState,
    onStateChange,
    isFetching,
    onApplyExternalFilters
  } = useTableData(getGroups, {
    filters: groupsFilters,
    sortBy: [{ id: "name", direction: "desc" }]
  });

  const filter = (filters, { setSubmitting }) => {
    onApplyExternalFilters(filters);
    setSubmitting(false);
  };

  const handleRowClick = useCallback(rowData => {
    history.push(buildGroupDetailInformationRoute(rowData.id));
  }, []);

  const handleOpenModalClick = useCallback(async () => {
    const group = await openAddGroupModal().result;
    if (group && group.data) {
      handleRowClick(group.data);
    }
  }, []);

  const handleStateChange = state => {
    const newFilters = state.filters || [];
    onStateChange({
      ...state,
      filters: [...newFilters, ...groupsFilters]
    });
  };

  return (
    <AdminPageLayout
      title={t("groupsFilters.title")}
      headerActions={
        <ButtonGroup align="left" flexWrap="wrap">
          {openReport && (
            <TooltipButton
              color="primary"
              variant="outlined"
              onClick={openReport}
              tooltipProps={{
                title: isGoldOrPlatinum
                  ? ""
                  : t("administration.goldPlatinumFeatureTooltip")
              }}
              disabled={!isGoldOrPlatinum}
            >
              {t("groupsFilters.openReport")}
            </TooltipButton>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={handleOpenModalClick}
            data-qa="add-course-btn"
          >
            {t("groupsFilters.addGroup")}
          </Button>
        </ButtonGroup>
      }
    >
      <Box mb={3}>
        <GroupsFilter onSubmit={filter} />
      </Box>
      <Table
        data={groups}
        columns={memoColumns}
        onStateChange={handleStateChange}
        onRowClick={handleRowClick}
        paginationState={paginationState}
        paginated
        loading={isFetching}
        initialSortBy={[{ id: "name", direction: "desc" }]}
        cellSize="medium"
      />
    </AdminPageLayout>
  );
};

const TranslatedGroups = withTranslation()(withRouter(Groups));

export default TranslatedGroups;
